import { createAsyncThunk } from "@reduxjs/toolkit";
import { APIError } from "../../constants/types";
import { api } from "../../api/api";
import { ERROR_MESSAGES, LoadState } from "../../constants/enums";
import {
  IOutlookCalenderEvent,
  IOutlookEmailComposePayload,
  IOutlookEventPayload,
  IOutlookFolder,
  IOutlookInboxEmail,
  IOutlookLabel,
  IOutlookMessageById,
  IOutlookNotification,
  OUTLOOK_MESSAGE_FLAGS,
} from "./outlook.types";
import { IStoreState } from "../initialStoreState";
import { disconnectOutlook } from "./outlookSlice";
import { getMailboxParam } from "../../helpers/outlookMailbox";
import { normalizeEmailList } from "../../helpers/email";

export const handleApiError = (error: any, dispatch: any) => {
  if (error?.response?.status === 407) {
    dispatch(disconnectOutlook());
  }
};

/**
 * Returns a specific error message for shared mailbox errors (403, 407)
 * when activeMailbox is non-null. Returns null if no shared-mailbox-specific
 * error handling applies, allowing the caller to fall through to generic handling.
 */
export const getSharedMailboxError = (
  error: any,
  activeMailbox: string | null,
): string | null => {
  if (!activeMailbox) return null;

  const status = error?.response?.status;
  if (status === 403) {
    return "Access to shared mailbox has been revoked or is unavailable";
  }
  if (status === 407) {
    return "Please reconnect your Outlook account with shared mailbox scopes to continue";
  }
  return null;
};

export const outlookConnectStatus = createAsyncThunk<
  {
    connected: boolean;
    email: string;
    expiresAt: string;
    isExpired: boolean;
  },
  {},
  { rejectValue: APIError }
>("outlook/fetchConnectStatus", async (_, { rejectWithValue, dispatch }) => {
  try {
    const finalUrl = `outlook/status`;
    const res = await api.get(finalUrl);
    const data = res.data.data.connected;

    return {
      connected: data,
      email: res.data.data.email,
      expiresAt: res.data.data.expiresAt,
      isExpired: res.data.data.isExpired,
    };
  } catch (err: any) {
    handleApiError(err, dispatch);
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const outlookInitiateAuth = createAsyncThunk<
  {
    url: string;
  },
  {
    redirect_uri: string;
  },
  { rejectValue: APIError }
>(
  "outlook/fetchConnectStatus",
  async ({ redirect_uri }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.get(
        `/outlook/auth/initiate?redirect_uri=${redirect_uri}`,
      );
      const data = res.data.data;

      return {
        url: data.authUrl,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const outlookDisconnect = createAsyncThunk<
  {
    data: string;
  },
  {},
  { rejectValue: APIError }
>("outlook/fetchConnectStatus", async (_, { rejectWithValue, dispatch }) => {
  try {
    await api.post("/outlook/disconnect");

    return {
      data: "Disconnected successfully",
    };
  } catch (err: any) {
    handleApiError(err, dispatch);
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const fetchChildFolders = createAsyncThunk<
  { parentFolderId: string; folders: IOutlookFolder[] },
  { folderId: string },
  { rejectValue: APIError }
>(
  "outlook/fetchChildFolders",
  async ({ folderId }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { outlook } = getState() as IStoreState;
      const finalUrl = `/outlook/folders/${folderId}/children${getMailboxParam(outlook.activeMailbox, `/outlook/folders/${folderId}/children`)}`;
      const res = await api.get(finalUrl);
      return {
        parentFolderId: folderId,
        folders: res.data.data,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      const { outlook } = getState() as IStoreState;
      const sharedMailboxError = getSharedMailboxError(err, outlook.activeMailbox);
      if (sharedMailboxError) {
        return rejectWithValue({
          message: sharedMailboxError,
          status: err.response?.status,
        });
      }
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
  {
    condition: ({ folderId }, { getState }) => {
      const { outlook } = getState() as IStoreState;
      if (outlook.childFolders[folderId]?.loading === LoadState.InProgress) {
        return false;
      }
      return true;
    },
  },
);

export const fetchOutlookFoldersList = createAsyncThunk<
  {
    folders: IOutlookFolder[];
  },
  {},
  { rejectValue: APIError }
>(
  "outlook/fetchOutlookFoldersList",
  async (_, { rejectWithValue, dispatch, getState }) => {
    try {
      const { outlook } = getState() as IStoreState;
      const finalUrl = `outlook/folders${getMailboxParam(outlook.activeMailbox, "outlook/folders")}`;
      const res = await api.get(finalUrl);

      return {
        folders: res.data.data,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      const { outlook } = getState() as IStoreState;
      const sharedMailboxError = getSharedMailboxError(err, outlook.activeMailbox);
      if (sharedMailboxError) {
        return rejectWithValue({
          message: sharedMailboxError,
          status: err.response?.status,
        });
      }
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const fetchOutlookFoldersEmailsList = createAsyncThunk<
  {
    emailList: IOutlookInboxEmail[];
    hasMore: boolean;
    nextLink: string | null;
  },
  {
    pageNumber: number;
    pageSize: number;
  },
  { rejectValue: APIError }
>(
  "outlook/fetchOutlookFoldersEmailsList",
  async ({ pageNumber, pageSize }, { rejectWithValue, getState, dispatch }) => {
    try {
      const { outlook } = getState() as IStoreState;
      let finalUrl = `outlook/folders/${outlook.selectedFolderId}/messages?page=${pageNumber}&pageSize=${pageSize}`;

      const filterParts: string[] = [];
      if (outlook.emailsMeta.labelFilter) {
        const matchedLabel = outlook.labels.list.find(
          (l) => l.id === outlook.emailsMeta.labelFilter,
        );
        filterParts.push(`label:${matchedLabel?.displayName || outlook.emailsMeta.labelFilter}`);
      }
      if (outlook.emailsMeta.readFilter !== "all") {
        filterParts.push(`isRead:${outlook.emailsMeta.readFilter === "read"}`);
      }
      if (outlook.emailsMeta.flagFilter !== "all") {
        filterParts.push(`flag:${outlook.emailsMeta.flagFilter}`);
      }

      // Advanced search filters
      const { advancedSearch } = outlook.emailsMeta;
      if (advancedSearch.hasAttachments !== "all") {
        filterParts.push(`hasAttachments:${advancedSearch.hasAttachments === "yes"}`);
      }
      if (advancedSearch.importance !== "all") {
        filterParts.push(`importance:${advancedSearch.importance}`);
      }
      if (advancedSearch.dateFrom) {
        filterParts.push(`receivedDateTime ge ${advancedSearch.dateFrom}`);
      }
      if (advancedSearch.dateTo) {
        filterParts.push(`receivedDateTime le ${advancedSearch.dateTo}`);
      }
      if (advancedSearch.from) {
        filterParts.push(`from:${advancedSearch.from}`);
      }
      if (advancedSearch.to) {
        filterParts.push(`to:${advancedSearch.to}`);
      }
      if (advancedSearch.subject) {
        filterParts.push(`subject:${advancedSearch.subject}`);
      }

      // Determine if we should use the search endpoint
      const hasAdvancedFilters =
        advancedSearch.from !== "" ||
        advancedSearch.to !== "" ||
        advancedSearch.subject !== "" ||
        advancedSearch.hasAttachments !== "all" ||
        advancedSearch.importance !== "all" ||
        advancedSearch.dateFrom !== "" ||
        advancedSearch.dateTo !== "";

      const useSearchEndpoint = !!outlook.emailsMeta.searchQuery || hasAdvancedFilters;

      if (useSearchEndpoint) {
        // Build the q parameter combining text query and advanced filters
        const qParts: string[] = [];
        if (outlook.emailsMeta.searchQuery) {
          qParts.push(outlook.emailsMeta.searchQuery);
        }
        if (advancedSearch.from) {
          qParts.push(`from:${advancedSearch.from}`);
        }
        if (advancedSearch.to) {
          qParts.push(`to:${advancedSearch.to}`);
        }
        if (advancedSearch.subject) {
          qParts.push(`subject:${advancedSearch.subject}`);
        }
        if (advancedSearch.hasAttachments !== "all") {
          qParts.push(`hasAttachments:${advancedSearch.hasAttachments === "yes"}`);
        }
        if (advancedSearch.importance !== "all") {
          qParts.push(`importance:${advancedSearch.importance}`);
        }
        if (advancedSearch.dateFrom) {
          qParts.push(`received>=${advancedSearch.dateFrom}`);
        }
        if (advancedSearch.dateTo) {
          qParts.push(`received<=${advancedSearch.dateTo}`);
        }
        if (outlook.emailsMeta.labelFilter) {
          const matchedLabel = outlook.labels.list.find(
            (l) => l.id === outlook.emailsMeta.labelFilter,
          );
          qParts.push(`label:${matchedLabel?.displayName || outlook.emailsMeta.labelFilter}`);
        }
        if (outlook.emailsMeta.readFilter !== "all") {
          qParts.push(`isRead:${outlook.emailsMeta.readFilter === "read"}`);
        }
        if (outlook.emailsMeta.flagFilter !== "all") {
          qParts.push(`flag:${outlook.emailsMeta.flagFilter}`);
        }

        finalUrl = `/outlook/messages/search?q=${encodeURIComponent(qParts.join(" "))}&pageSize=${pageSize}`;
        if (outlook.emailsMeta.searchNextLink) {
          finalUrl += `&nextLink=${encodeURIComponent(outlook.emailsMeta.searchNextLink)}`;
        }
      } else {
        if (filterParts.length > 0) {
          finalUrl += `&filter=${encodeURIComponent(filterParts.join(","))}`;
        }
      }
      finalUrl += getMailboxParam(outlook.activeMailbox, finalUrl);
      const res = await api.get(finalUrl);

      return {
        emailList: res.data.data,
        hasMore: res.data.data.length !== 0,
        nextLink: res.data.meta?.nextLink || res.data.nextLink || null,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      const { outlook } = getState() as IStoreState;
      const sharedMailboxError = getSharedMailboxError(err, outlook.activeMailbox);
      if (sharedMailboxError) {
        return rejectWithValue({
          message: sharedMailboxError,
          status: err.response?.status,
        });
      }
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
  {
    condition: (_, { getState }) => {
      const { outlook } = getState() as IStoreState;
      if (
        outlook.emails.loading === LoadState.InProgress ||
        !outlook.selectedFolderId
      ) {
        return false;
      }
      return true;
    },
  },
);

export const fetchOutlookInboxByMessageId = createAsyncThunk<
  {
    message: IOutlookMessageById;
  },
  {
    messageId: string;
    mailboxEmail?: string;
  },
  { rejectValue: APIError }
>(
  "outlook/fetchOutlookInboxByMessageId",
  async ({ messageId, mailboxEmail }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { outlook } = getState() as IStoreState;
      const effectiveMailbox = mailboxEmail ?? outlook.activeMailbox;
      const finalUrl = `/outlook/messages/${messageId}${getMailboxParam(effectiveMailbox, `/outlook/messages/${messageId}`)}`;
      const res = await api.get(finalUrl);

      return {
        message: res.data.data,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      const { outlook } = getState() as IStoreState;
      const sharedMailboxError = getSharedMailboxError(err, outlook.activeMailbox);
      if (sharedMailboxError) {
        return rejectWithValue({
          message: sharedMailboxError,
          status: err.response?.status,
        });
      }
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const fetchOutlookNotifications = createAsyncThunk<
  {
    notifications: IOutlookNotification[];
  },
  {
    pageNumber: number;
    pageSize: number;
  },
  { rejectValue: APIError }
>(
  "outlook/fetchOutlookNotifications",
  async ({ pageNumber, pageSize }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { outlook } = getState() as IStoreState;
      const finalUrl = `/outlook/notifications?read_status=UNREAD&pageNo=${pageNumber}&itemPerPage=${pageSize}${getMailboxParam(outlook.activeMailbox, `/outlook/notifications?read_status=UNREAD&pageNo=${pageNumber}&itemPerPage=${pageSize}`)}`;
      const res = await api.get(finalUrl);

      return {
        notifications: res.data.data,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      const { outlook } = getState() as IStoreState;
      const sharedMailboxError = getSharedMailboxError(err, outlook.activeMailbox);
      if (sharedMailboxError) {
        return rejectWithValue({
          message: sharedMailboxError,
          status: err.response?.status,
        });
      }
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const markReadEmailNotification = createAsyncThunk<
  {
    data: string;
    messageId: string;
  },
  {
    messageId: string;
    userfactId: number;
  },
  { rejectValue: APIError }
>(
  "outlook/markReadEmailNotification",
  async ({ messageId, userfactId }, { rejectWithValue, dispatch }) => {
    try {
      await api.patch(`/outlook/notifications/read`, {
        message_id: messageId,
        user_fact_id: userfactId,
      });

      return {
        data: "Notification marked as read",
        messageId,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const markReadUnreadEmailInOutlook = createAsyncThunk<
  {
    data: string;
    messageId: string;
    isRead: boolean;
  },
  {
    messageId: string;
    isRead?: boolean;
  },
  { rejectValue: APIError }
>(
  "outlook/markReadUnreadEmailInOutlook",
  async ({ messageId, isRead = true }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { outlook } = getState() as IStoreState;
      const finalUrl = `/outlook/messages/${messageId}/read-status${getMailboxParam(outlook.activeMailbox, `/outlook/messages/${messageId}/read-status`)}`;
      await api.patch(finalUrl, {
        isRead: isRead ?? true,
      });
      return {
        data: isRead
          ? "Notification marked as read"
          : "Notification marked as unread",
        messageId,
        isRead,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      const { outlook } = getState() as IStoreState;
      const sharedMailboxError = getSharedMailboxError(err, outlook.activeMailbox);
      if (sharedMailboxError) {
        return rejectWithValue({
          message: sharedMailboxError,
          status: err.response?.status,
        });
      }
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const sendOutlookEmail = createAsyncThunk<
  {
    data: string;
    messageId: string;
  },
  {
    payload: IOutlookEmailComposePayload;
    /** Optional override: send from a specific mailbox instead of the active one */
    fromMailbox?: string | null;
  },
  { rejectValue: APIError }
>(
  "outlook/sendOutlookEmail",
  async ({ payload, fromMailbox }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { outlook } = getState() as IStoreState;
      const mailbox = fromMailbox !== undefined ? fromMailbox : outlook.activeMailbox;
      const finalUrl = `/outlook/send${getMailboxParam(mailbox, "/outlook/send")}`;
      // Canonicalize recipient addresses to lowercase before sending.
      const normalizedPayload: IOutlookEmailComposePayload = {
        ...payload,
        to: normalizeEmailList(payload.to),
        cc: normalizeEmailList(payload.cc),
        bcc: normalizeEmailList(payload.bcc),
      };
      await api.post(finalUrl, normalizedPayload);
      return {
        data: "Email sent successfully",
        messageId: "",
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      if (err.response?.data?.error === "ErrorSendAsDenied") {
        return rejectWithValue({
          message: "You do not have Send As permission for this shared mailbox",
          status: err.response?.status,
        });
      }
      const { outlook } = getState() as IStoreState;
      const mailboxForError = fromMailbox !== undefined ? fromMailbox : outlook.activeMailbox;
      const sharedMailboxError = getSharedMailboxError(err, mailboxForError);
      if (sharedMailboxError) {
        return rejectWithValue({
          message: sharedMailboxError,
          status: err.response?.status,
        });
      }
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const deleteOutlookMessageById = createAsyncThunk<
  {
    messageIds: string[];
  },
  {
    messageIds: string[];
  },
  { rejectValue: APIError }
>(
  "outlook/deleteOutlookMessageById",
  async ({ messageIds }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { outlook } = getState() as IStoreState;
      const query = messageIds.map((id) => `messageIds=${id}`).join("&");
      const finalUrl = `/outlook/messages-v2?${query}${getMailboxParam(outlook.activeMailbox, `/outlook/messages-v2?${query}`)}`;
      await api.delete(finalUrl);
      return {
        messageIds,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      const { outlook } = getState() as IStoreState;
      const sharedMailboxError = getSharedMailboxError(err, outlook.activeMailbox);
      if (sharedMailboxError) {
        return rejectWithValue({
          message: sharedMailboxError,
          status: err.response?.status,
        });
      }
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const createFolder = createAsyncThunk<
  {
    data: string;
  },
  {
    folderName: string;
    parentFolderId?: string;
  },
  { rejectValue: APIError }
>(
  "outlook/createFolder",
  async ({ folderName, parentFolderId }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { outlook } = getState() as IStoreState;
      const finalUrl = `/outlook/folders${getMailboxParam(outlook.activeMailbox, "/outlook/folders")}`;
      await api.post(finalUrl, {
        displayName: folderName,
        parentFolderId: parentFolderId,
      });
      return {
        data: "Folder created successfully",
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      const { outlook } = getState() as IStoreState;
      const sharedMailboxError = getSharedMailboxError(err, outlook.activeMailbox);
      if (sharedMailboxError) {
        return rejectWithValue({
          message: sharedMailboxError,
          status: err.response?.status,
        });
      }
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const deleteOutlookFolder = createAsyncThunk<
  {
    folderId: string;
  },
  {
    folderId: string;
  },
  { rejectValue: APIError }
>(
  "outlook/deleteOutlookFolder",
  async ({ folderId }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { outlook } = getState() as IStoreState;
      const finalUrl = `/outlook/folders/${folderId}${getMailboxParam(outlook.activeMailbox, `/outlook/folders/${folderId}`)}`;
      await api.delete(finalUrl);
      return {
        folderId,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      const { outlook } = getState() as IStoreState;
      const sharedMailboxError = getSharedMailboxError(err, outlook.activeMailbox);
      if (sharedMailboxError) {
        return rejectWithValue({
          message: sharedMailboxError,
          status: err.response?.status,
        });
      }
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const changeMessageFlag = createAsyncThunk<
  {
    data: string;
    messageId: string;
    flag: OUTLOOK_MESSAGE_FLAGS;
    dueDateTime?: string;
  },
  {
    messageId: string;
    flag: OUTLOOK_MESSAGE_FLAGS;
    dueDateTime?: string;
  },
  { rejectValue: APIError }
>(
  "outlook/changeMessageFlag",
  async ({ messageId, flag, dueDateTime }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { outlook } = getState() as IStoreState;
      const finalUrl = `/outlook/messages/${messageId}/flag${getMailboxParam(outlook.activeMailbox, `/outlook/messages/${messageId}/flag`)}`;
      await api.patch(finalUrl, {
        flagStatus: flag,
        dueDateTime: dueDateTime,
      });
      return {
        data: "Flag updated successfully",
        messageId,
        flag,
        dueDateTime,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      const { outlook } = getState() as IStoreState;
      const sharedMailboxError = getSharedMailboxError(err, outlook.activeMailbox);
      if (sharedMailboxError) {
        return rejectWithValue({
          message: sharedMailboxError,
          status: err.response?.status,
        });
      }
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const fetchOutlookLabels = createAsyncThunk<
  {
    labels: IOutlookLabel[];
  },
  {},
  { rejectValue: APIError }
>("outlook/fetchOutlookLabels", async (_, { rejectWithValue, dispatch, getState }) => {
  try {
    const { outlook } = getState() as IStoreState;
    const finalUrl = `/outlook/labels${getMailboxParam(outlook.activeMailbox, "/outlook/labels")}`;
    const res = await api.get(finalUrl);

    return {
      labels: res.data.data,
    };
  } catch (err: any) {
    handleApiError(err, dispatch);
    const { outlook } = getState() as IStoreState;
    const sharedMailboxError = getSharedMailboxError(err, outlook.activeMailbox);
    if (sharedMailboxError) {
      return rejectWithValue({
        message: sharedMailboxError,
        status: err.response?.status,
      });
    }
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const createLabel = createAsyncThunk<
  {
    label: IOutlookLabel;
  },
  {
    labelName: string;
    color: string;
  },
  { rejectValue: APIError }
>(
  "outlook/createLabel",
  async ({ labelName, color }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { outlook } = getState() as IStoreState;
      const finalUrl = `/outlook/labels${getMailboxParam(outlook.activeMailbox, "/outlook/labels")}`;
      const res = await api.post(finalUrl, {
        displayName: labelName,
        color: color,
      });
      return {
        label: res.data.data,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      const { outlook } = getState() as IStoreState;
      const sharedMailboxError = getSharedMailboxError(err, outlook.activeMailbox);
      if (sharedMailboxError) {
        return rejectWithValue({
          message: sharedMailboxError,
          status: err.response?.status,
        });
      }
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const assignLabelToMessage = createAsyncThunk<
  {
    data: string[];
    messageId: string;
  },
  {
    messageId: string;
    labels: string[];
  },
  { rejectValue: APIError }
>(
  "outlook/assignLabelToMessage",
  async ({ messageId, labels }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { outlook } = getState() as IStoreState;
      const finalUrl = `/outlook/messages/${messageId}/label${getMailboxParam(outlook.activeMailbox, `/outlook/messages/${messageId}/label`)}`;
      const res = await api.patch(finalUrl, {
        categories: labels,
      });
      return {
        data: res.data.data,
        messageId,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      const { outlook } = getState() as IStoreState;
      const sharedMailboxError = getSharedMailboxError(err, outlook.activeMailbox);
      if (sharedMailboxError) {
        return rejectWithValue({
          message: sharedMailboxError,
          status: err.response?.status,
        });
      }
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const replyOutlookEmail = createAsyncThunk<
  {
    data: string[];
    messageId: string;
  },
  {
    messageId: string;
    comment: string;
    replyAll: boolean;
    attachments?: { name: string; contentType: string; contentBytes: string }[];
  },
  { rejectValue: APIError }
>(
  "outlook/replyOutlookEmail",
  async ({ messageId, comment, replyAll, attachments }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { outlook } = getState() as IStoreState;
      const payload: any = {
        comment,
        replyAll,
      };
      if (attachments && attachments.length > 0) {
        payload.attachments = attachments;
      }
      const finalUrl = `/outlook/messages/${messageId}/reply${getMailboxParam(outlook.activeMailbox, `/outlook/messages/${messageId}/reply`)}`;
      const res = await api.post(finalUrl, payload);
      return {
        data: res.data.data,
        messageId,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      const { outlook } = getState() as IStoreState;
      const sharedMailboxError = getSharedMailboxError(err, outlook.activeMailbox);
      if (sharedMailboxError) {
        return rejectWithValue({
          message: sharedMailboxError,
          status: err.response?.status,
        });
      }
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const forwardOutlookEmail = createAsyncThunk<
  {
    data: string[];
    messageId: string;
  },
  {
    messageId: string;
    to: string[];
    cc: string[];
    comment: string;
    attachments?: { name: string; contentType: string; contentBytes: string }[];
    /** Optional override: forward from a specific mailbox instead of the active one */
    fromMailbox?: string | null;
  },
  { rejectValue: APIError }
>(
  "outlook/forwardOutlookEmail",
  async ({ messageId, to, cc, comment, attachments, fromMailbox }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { outlook } = getState() as IStoreState;
      const mailbox = fromMailbox !== undefined ? fromMailbox : outlook.activeMailbox;
      const payload: any = {
        to: normalizeEmailList(to),
        cc: normalizeEmailList(cc),
        comment,
      };
      if (attachments && attachments.length > 0) {
        payload.attachments = attachments;
      }
      const finalUrl = `/outlook/messages/${messageId}/forward${getMailboxParam(mailbox, `/outlook/messages/${messageId}/forward`)}`;
      const res = await api.post(finalUrl, payload);
      return {
        data: res.data.data,
        messageId,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      const { outlook } = getState() as IStoreState;
      const mailboxForError = fromMailbox !== undefined ? fromMailbox : outlook.activeMailbox;
      const sharedMailboxError = getSharedMailboxError(err, mailboxForError);
      if (sharedMailboxError) {
        return rejectWithValue({
          message: sharedMailboxError,
          status: err.response?.status,
        });
      }
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const logoutOutlook = createAsyncThunk<
  {
    data: string;
  },
  {},
  { rejectValue: APIError }
>("outlook/logoutOutlook", async (_, { rejectWithValue, dispatch }) => {
  try {
    await api.post(`/outlook/disconnect`);
    return {
      data: "Outlook disconnected successfully",
    };
  } catch (err: any) {
    handleApiError(err, dispatch);
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const archiveOutlookMessageById = createAsyncThunk<
  {
    messageIds: string[];
  },
  {
    messageIds: string[];
  },
  { rejectValue: APIError }
>(
  "outlook/archiveOutlookMessageById",
  async ({ messageIds }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { outlook } = getState() as IStoreState;
      const query = messageIds.map((id) => `messageIds=${id}`).join("&");
      const finalUrl = `/outlook/messages/archive-v2?${query}${getMailboxParam(outlook.activeMailbox, `/outlook/messages/archive-v2?${query}`)}`;
      await api.post(finalUrl);
      return {
        messageIds,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      const { outlook } = getState() as IStoreState;
      const sharedMailboxError = getSharedMailboxError(err, outlook.activeMailbox);
      if (sharedMailboxError) {
        return rejectWithValue({
          message: sharedMailboxError,
          status: err.response?.status,
        });
      }
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const attachPolicyToEmail = createAsyncThunk<
  {
    data: string;
    messageId: string;
  },
  {
    messageId: string;
    customerPolicyId: string;
    email_history_code: string;
    module_name: string;
    status: string;
    comments: string;
  },
  { rejectValue: APIError }
>(
  "outlook/attachPolicyToEmail",
  async (
    { messageId, customerPolicyId, email_history_code, module_name, status, comments },
    { rejectWithValue, dispatch },
  ) => {
    try {
      await api.post(`/outlook/upsert-outlook-email-history`, {
        messageId: messageId,
        customer_policy_id: customerPolicyId,
        email_history_code,
        module_name,
        status,
        comment: comments,
      });
      return {
        data: "Policy attached successfully",
        messageId,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const fetchCalenderEvents = createAsyncThunk<
  {
    data: IOutlookCalenderEvent[];
  },
  {
    startDateTime: string;
    endDateTime: string;
  },
  { rejectValue: APIError }
>("outlook/fetchCalenderEvents", async ({ startDateTime, endDateTime }, { rejectWithValue, dispatch, getState }) => {
  try {
    const { outlook } = getState() as IStoreState;
    const baseUrl = `/outlook/calendar/events?startDateTime=${startDateTime}&endDateTime=${endDateTime}`;
    const finalUrl = `${baseUrl}${getMailboxParam(outlook.activeMailbox, baseUrl)}`;
    const res = await api.get(finalUrl);
    return {
      data: res.data.data,
    };
  } catch (err: any) {
    handleApiError(err, dispatch);
    const { outlook } = getState() as IStoreState;
    const sharedMailboxError = getSharedMailboxError(err, outlook.activeMailbox);
    if (sharedMailboxError) {
      return rejectWithValue({
        message: sharedMailboxError,
        status: err.response?.status,
      });
    }
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const upsertOulookEvent = createAsyncThunk<
  {
    data: string;
  },
  {
    eventData: IOutlookEventPayload;
    eventId?: string;
  },
  { rejectValue: APIError }
>(
  "outlook/upsertOulookEvent",
  async ({ eventData, eventId }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { outlook } = getState() as IStoreState;
      if (eventId) {
        const finalUrl = `/outlook/calendar/events/${eventId}${getMailboxParam(outlook.activeMailbox, `/outlook/calendar/events/${eventId}`)}`;
        await api.patch(finalUrl, eventData);
      } else {
        const finalUrl = `/outlook/calendar/events${getMailboxParam(outlook.activeMailbox, "/outlook/calendar/events")}`;
        await api.post(finalUrl, eventData);
      }
      return {
        data: "Event created successfully",
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      const { outlook } = getState() as IStoreState;
      const sharedMailboxError = getSharedMailboxError(err, outlook.activeMailbox);
      if (sharedMailboxError) {
        return rejectWithValue({
          message: sharedMailboxError,
          status: err.response?.status,
        });
      }
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const fetchOutlookEventById = createAsyncThunk<
  {
    data: IOutlookCalenderEvent;
  },
  {
    eventId: string;
  },
  { rejectValue: APIError }
>(
  "outlook/fetchOutlookEventById",
  async ({ eventId }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { outlook } = getState() as IStoreState;
      const finalUrl = `/outlook/calendar/events/${eventId}${getMailboxParam(outlook.activeMailbox, `/outlook/calendar/events/${eventId}`)}`;
      const res = await api.get(finalUrl);

      return {
        data: res.data.data,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      const { outlook } = getState() as IStoreState;
      const sharedMailboxError = getSharedMailboxError(err, outlook.activeMailbox);
      if (sharedMailboxError) {
        return rejectWithValue({
          message: sharedMailboxError,
          status: err.response?.status,
        });
      }
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const deleteOutlookEvent = createAsyncThunk<
  {
    data: string;
  },
  {
    eventId: string;
  },
  { rejectValue: APIError }
>(
  "outlook/deleteOutlookEvent",
  async ({ eventId }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { outlook } = getState() as IStoreState;
      const finalUrl = `/outlook/calendar/events/${eventId}${getMailboxParam(outlook.activeMailbox, `/outlook/calendar/events/${eventId}`)}`;
      const res = await api.delete(finalUrl);
      return {
        data: "Event deleted successfully",
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      const { outlook } = getState() as IStoreState;
      const sharedMailboxError = getSharedMailboxError(err, outlook.activeMailbox);
      if (sharedMailboxError) {
        return rejectWithValue({
          message: sharedMailboxError,
          status: err.response?.status,
        });
      }
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);


export const moveOutlookMessagesToFolder = createAsyncThunk<
  { folderId: string; messageIds: string[] },
  { folderId: string; messageIds: string[] },
  { rejectValue: APIError }
>(
  "outlook/moveOutlookMessagesToFolder",
  async ({ folderId, messageIds }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { outlook } = getState() as IStoreState;
      const finalUrl = `/outlook/messages/move${getMailboxParam(outlook.activeMailbox, "/outlook/messages/move")}`;
      await api.post(finalUrl, { folderId, messageIds });
      return {
        folderId,
        messageIds,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      const { outlook } = getState() as IStoreState;
      const sharedMailboxError = getSharedMailboxError(err, outlook.activeMailbox);
      if (sharedMailboxError) {
        return rejectWithValue({
          message: sharedMailboxError,
          status: err.response?.status,
        });
      }
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const batchMarkReadUnreadInOutlook = createAsyncThunk<
  { messageIds: string[]; isRead: boolean },
  { messageIds: string[]; isRead: boolean },
  { rejectValue: APIError }
>(
  "outlook/batchMarkReadUnreadInOutlook",
  async ({ messageIds, isRead }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { outlook } = getState() as IStoreState;
      await Promise.all(
        messageIds.map((messageId) => {
          const finalUrl = `/outlook/messages/${messageId}/read-status${getMailboxParam(outlook.activeMailbox, `/outlook/messages/${messageId}/read-status`)}`;
          return api.patch(finalUrl, { isRead });
        }),
      );
      return {
        messageIds,
        isRead,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      const { outlook } = getState() as IStoreState;
      const sharedMailboxError = getSharedMailboxError(err, outlook.activeMailbox);
      if (sharedMailboxError) {
        return rejectWithValue({
          message: sharedMailboxError,
          status: err.response?.status,
        });
      }
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const downloadOutlookEmail = createAsyncThunk<
  {
    data: IOutlookCalenderEvent;
  },
  {
    messageId: string;
  },
  { rejectValue: APIError }
>(
  "outlook/downloadOutlookEmail",
  async ({ messageId }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { outlook } = getState() as IStoreState;
      const finalUrl = `/outlook/messages/${messageId}/raw${getMailboxParam(outlook.activeMailbox, `/outlook/messages/${messageId}/raw`)}`;
      const res = await api.get(finalUrl);
      return res;
    } catch (err: any) {
      handleApiError(err, dispatch);
      const { outlook } = getState() as IStoreState;
      const sharedMailboxError = getSharedMailboxError(err, outlook.activeMailbox);
      if (sharedMailboxError) {
        return rejectWithValue({
          message: sharedMailboxError,
          status: err.response?.status,
        });
      }
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const fetchAllInboxesEmails = createAsyncThunk<
  {
    emailList: IOutlookInboxEmail[];
    hasMore: boolean;
    nextLink: string | null;
  },
  {
    pageNumber: number;
    pageSize: number;
  },
  { rejectValue: APIError }
>(
  "outlook/fetchAllInboxesEmails",
  async ({ pageNumber, pageSize }, { rejectWithValue, getState, dispatch }) => {
    try {
      const { outlook } = getState() as IStoreState;
      let finalUrl = `/outlook/all-mails-inbox?page=${pageNumber}&pageSize=${pageSize}`;
      if (outlook.emailsMeta.searchNextLink) {
        finalUrl += `&nextLink=${encodeURIComponent(outlook.emailsMeta.searchNextLink)}`;
      }
      const res = await api.get(finalUrl);
      return {
        emailList: res.data.data,
        hasMore: res.data.data.length > 0,
        nextLink: res.data.meta?.nextLink || res.data.nextLink || null,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
  {
    condition: (_, { getState }) => {
      const { outlook } = getState() as IStoreState;
      if (outlook.emails.loading === LoadState.InProgress) {
        return false;
      }
      return true;
    },
  },
);
